import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Translate as TranslateIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useExperiences,
  useUpdateExperience,
  useDeleteExperience,
} from "../../hooks/useExperiences";
import { useTechnologies } from "../../hooks/useTechnologies";
import { useSkills } from "../../hooks/useSkills";
import { supabase } from "../../lib/supabase";
import {
  adminColors,
  textFieldStyles,
  dialogStyles,
  tableStyles,
  buttonStyles,
  switchStyles,
} from "./adminStyles";
import { useTranslation } from "react-i18next";

const AdminExperiences = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data: experiences, isLoading, refetch } = useExperiences();
  const { data: technologies } = useTechnologies();
  const { data: skills } = useSkills({ language: i18n.language });
  const updateExperience = useUpdateExperience();
  const deleteExperience = useDeleteExperience();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    company: "",
    start_date: "",
    end_date: "",
    is_current: false,
    selectedTechs: [],
    selectedSkills: [],
    translations: {
      fr: { role: "", description: "" },
      en: { role: "", description: "" },
    },
  });

  const handleOpenCreate = () => {
    setSelectedExperience(null);
    setFormData({
      company: "",
      start_date: "",
      end_date: "",
      is_current: false,
      selectedTechs: [],
      translations: {
        fr: { role: "", description: "" },
        en: { role: "", description: "" },
      },
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (exp) => {
    setSelectedExperience(exp);
    const frTrans =
      exp.translations?.find((t) => t.language_code === "fr") || {};
    const enTrans =
      exp.translations?.find((t) => t.language_code === "en") || {};

    // Fetch related techs
    const { data: relTechs } = await supabase
      .from("experience_technologies")
      .select("technology_id")
      .eq("experience_id", exp.id);

    // Fetch related skills
    const { data: relSkills } = await supabase
      .from("experience_skills")
      .select("skill_id")
      .eq("experience_id", exp.id);

    const initialTechs = relTechs
      ? technologies?.filter((t) =>
          relTechs.some((rt) => rt.technology_id === t.id)
        ) || []
      : [];

    const initialSkills = relSkills
      ? skills?.filter((s) => relSkills.some((rs) => rs.skill_id === s.id)) ||
        []
      : [];

    setFormData({
      company: exp.company || "",
      start_date: exp.start_date || "",
      end_date: exp.end_date || "",
      is_current: exp.is_current || false,
      selectedTechs: initialTechs,
      selectedSkills: initialSkills,
      translations: {
        fr: {
          role: frTrans.role || "",
          description: frTrans.description || "",
        },
        en: {
          role: enTrans.role || "",
          description: enTrans.description || "",
        },
      },
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenDelete = (exp) => {
    setSelectedExperience(exp);
    setDeleteDialogOpen(true);
  };

  const handleMove = async (exp, direction) => {
    const currentIndex = experiences.findIndex((e) => e.id === exp.id);
    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= experiences.length) return;

    const targetExp = experiences[targetIndex];

    try {
      const tempOrder = exp.display_order || 0;
      await supabase
        .from("experiences")
        .update({ display_order: targetExp.display_order || 0 })
        .eq("id", exp.id);
      await supabase
        .from("experiences")
        .update({ display_order: tempOrder })
        .eq("id", targetExp.id);
      await refetch();
    } catch (err) {
      console.error("Error reordering experiences:", err);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const expData = {
        company: formData.company,
        start_date: formData.start_date || null,
        end_date: formData.is_current ? null : formData.end_date || null,
        is_current: formData.is_current,
      };

      let expId;

      if (selectedExperience) {
        await updateExperience.mutateAsync({
          id: selectedExperience.id,
          ...expData,
        });
        expId = selectedExperience.id;

        for (const lang of ["fr", "en"]) {
          const transData = {
            experience_id: expId,
            language_code: lang,
            role: formData.translations[lang].role,
            description: formData.translations[lang].description,
          };

          const { data: existing } = await supabase
            .from("experiences_translations")
            .select("id")
            .eq("experience_id", expId)
            .eq("language_code", lang)
            .maybeSingle();

          if (existing) {
            await supabase
              .from("experiences_translations")
              .update(transData)
              .eq("id", existing.id);
          } else {
            await supabase
              .from("experiences_translations")
              .insert({ id: crypto.randomUUID(), ...transData });
          }
        }

        // Update technologies
        await supabase
          .from("experience_technologies")
          .delete()
          .eq("experience_id", expId);
        // Update skills
        await supabase
          .from("experience_skills")
          .delete()
          .eq("experience_id", expId);
      } else {
        const result = await supabase.from("experiences").insert(expData).select().single();
        if (result.error) throw result.error;
        expId = result.data.id;

        for (const lang of ["fr", "en"]) {
          await supabase.from("experiences_translations").insert({
            id: crypto.randomUUID(),
            experience_id: expId,
            language_code: lang,
            role: formData.translations[lang].role,
            description: formData.translations[lang].description,
          });
        }
      }

      // Batch insert technologies
      if (formData.selectedTechs.length > 0) {
        const techInserts = formData.selectedTechs.map((tech) => ({
          experience_id: expId,
          technology_id: tech.id,
        }));
        await supabase.from("experience_technologies").insert(techInserts);
      }

      // Batch insert skills
      if (formData.selectedSkills.length > 0) {
        const skillInserts = formData.selectedSkills.map((skill) => ({
          experience_id: expId,
          skill_id: skill.id,
        }));
        await supabase.from("experience_skills").insert(skillInserts);
      }

      await refetch();
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving experience:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteExperience.mutateAsync(selectedExperience.id);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting experience:", err);
    }
  };

  const updateTranslation = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], [field]: value },
      },
    }));
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: adminColors.bg.page,
        }}
      >
        <CircularProgress sx={{ color: adminColors.accent.teal }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: "100vh", bgcolor: adminColors.bg.page, pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/admin")} sx={{ color: "white" }}><BackIcon /></IconButton>
            <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>{t("admin.sections.experiences")}</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={buttonStyles.primary}>{t("admin.actions.create")}</Button>
        </Box>

        <TableContainer component={Paper} sx={tableStyles.container}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.company")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.role")} (FR)
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.order")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.date")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell} align="right">
                  {t("admin.labels.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {experiences?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{
                      ...tableStyles.bodyCell,
                      textAlign: "center",
                      py: 4,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {t("admin.messages.noData")}
                  </TableCell>
                </TableRow>
              )}
              {experiences?.map((exp) => (
                <TableRow key={exp.id} sx={tableStyles.row}>
                  <TableCell sx={tableStyles.bodyCell}>{exp.company}</TableCell>
                  <TableCell
                    sx={{
                      ...tableStyles.bodyCell,
                      color: adminColors.accent.teal,
                    }}
                  >
                    {exp.role || "-"}
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Stack direction="row">
                      <IconButton
                        size="small"
                        onClick={() => handleMove(exp, "up")}
                        disabled={experiences.indexOf(exp) === 0}
                        sx={{ color: adminColors.accent.teal }}
                      >
                        <UpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMove(exp, "down")}
                        disabled={
                          experiences.indexOf(exp) === experiences.length - 1
                        }
                        sx={{ color: adminColors.accent.teal }}
                      >
                        <DownIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableStyles.bodyCell,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {exp.start_date} -{" "}
                    {exp.is_current
                      ? t("admin.labels.isCurrent")
                      : exp.end_date || "-"}
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell} align="right">
                    <IconButton
                      onClick={() => handleOpenEdit(exp)}
                      sx={buttonStyles.iconEdit}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDelete(exp)}
                      sx={buttonStyles.iconDelete}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: adminColors.text.primary,
              borderBottom: `1px solid ${adminColors.border.default}`,
              pb: 2,
            }}
          >
            <TranslateIcon sx={{ color: adminColors.accent.teal }} />
            {selectedExperience
              ? `${t("admin.actions.edit")} ${t("admin.sections.experiences")}`
              : `${t("admin.actions.create")} ${t(
                  "admin.sections.experiences"
                )}`}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label={t("admin.labels.company")}
                fullWidth
                value={formData.company}
                onChange={(e) =>
                  setFormData({ ...formData, company: e.target.value })
                }
                sx={textFieldStyles}
              />

              <Autocomplete
                multiple
                options={technologies || []}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={formData.selectedTechs}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, selectedTechs: newValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.sections.technologies")}
                    placeholder={t("messages.noResults")}
                    sx={textFieldStyles}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.id}
                      label={option.name}
                      {...getTagProps({ index })}
                      sx={{ bgcolor: adminColors.accent.teal, color: "white" }}
                    />
                  ))
                }
                PaperProps={{
                  sx: { bgcolor: adminColors.bg.card, color: "white" },
                }}
              />

              <Autocomplete
                multiple
                options={skills || []}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={formData.selectedSkills}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, selectedSkills: newValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.sections.skills")}
                    placeholder={t("messages.noResults")}
                    sx={textFieldStyles}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.id}
                      label={option.name}
                      {...getTagProps({ index })}
                      sx={{
                        bgcolor: adminColors.accent.gold,
                        color: "#0B0F14",
                      }}
                    />
                  ))
                }
                PaperProps={{
                  sx: { bgcolor: adminColors.bg.card, color: "white" },
                }}
              />

              <Divider sx={{ borderColor: adminColors.border.default }} />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: adminColors.accent.gold,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: 600,
                  }}
                >
                  <TranslateIcon fontSize="small" />{" "}
                  {t("admin.translations.title")}
                </Typography>
                <Tabs
                  value={tabValue}
                  onChange={(e, v) => setTabValue(v)}
                  sx={{
                    mb: 2,
                    "& .MuiTabs-indicator": {
                      bgcolor: adminColors.accent.teal,
                    },
                  }}
                >
                  <Tab
                    label={t("admin.translations.tabs.fr")}
                    sx={{
                      color:
                        tabValue === 0
                          ? adminColors.accent.teal
                          : adminColors.text.secondary,
                      fontWeight: tabValue === 0 ? 600 : 400,
                    }}
                  />
                  <Tab
                    label={t("admin.translations.tabs.en")}
                    sx={{
                      color:
                        tabValue === 1
                          ? adminColors.accent.teal
                          : adminColors.text.secondary,
                      fontWeight: tabValue === 1 ? 600 : 400,
                    }}
                  />
                </Tabs>

                {tabValue === 0 && (
                  <Stack spacing={2}>
                    <TextField
                      label={`${t("admin.labels.role")} (FR)`}
                      fullWidth
                      value={formData.translations.fr.role}
                      onChange={(e) =>
                        updateTranslation("fr", "role", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t("admin.labels.description")} (FR)`}
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.translations.fr.description}
                      onChange={(e) =>
                        updateTranslation("fr", "description", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                  </Stack>
                )}

                {tabValue === 1 && (
                  <Stack spacing={2}>
                    <TextField
                      label={`${t("admin.labels.role")} (EN)`}
                      fullWidth
                      value={formData.translations.en.role}
                      onChange={(e) =>
                        updateTranslation("en", "role", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t("admin.labels.description")} (EN)`}
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.translations.en.description}
                      onChange={(e) =>
                        updateTranslation("en", "description", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                  </Stack>
                )}
              </Box>

              <Divider sx={{ borderColor: adminColors.border.default }} />

              <Stack direction="row" spacing={2}>
                <TextField
                  label={t("admin.labels.startDate")}
                  fullWidth
                  type="date"
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={textFieldStyles}
                />
                <TextField
                  label={t("admin.labels.endDate")}
                  fullWidth
                  type="date"
                  value={formData.end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  disabled={formData.is_current}
                  sx={textFieldStyles}
                />
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_current}
                    onChange={(e) =>
                      setFormData({ ...formData, is_current: e.target.checked })
                    }
                    sx={switchStyles}
                  />
                }
                label={t("admin.labels.isCurrent")}
                sx={{ color: adminColors.text.primary }}
              />
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{ p: 3, borderTop: `1px solid ${adminColors.border.default}` }}
          >
            <Button
              onClick={() => setDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={saving}
              sx={buttonStyles.primary}
            >
              {saving ? (
                <CircularProgress
                  size={24}
                  sx={{ color: adminColors.bg.page }}
                />
              ) : (
                t("admin.actions.save")
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle sx={{ color: adminColors.text.primary }}>
            {t("admin.messages.confirmDeleteTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: adminColors.text.secondary }}>
              {t("admin.messages.confirmDelete")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              disabled={deleteExperience.isPending}
              sx={{
                bgcolor: adminColors.status.error,
                "&:hover": { bgcolor: "#DC2626" },
              }}
            >
              {deleteExperience.isPending ? (
                <CircularProgress size={24} />
              ) : (
                t("admin.actions.delete")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminExperiences;
